import React, {useState} from "react";
import {AlertCircle, ArrowLeft, Library, X} from "lucide-react";
import LearningScreen, {Word} from "./learning/LearningScreen";
import api from "../api/axios";
import {useAuth} from "../features/auth/AuthContext";

type ViewMode = 'LEVEL_SELECT' | 'CHAPTER_SELECT' | 'LEARNING';

// 레벨별 테마 색상 및 설명 설정
const LEVEL_CONFIG: Record<string, { color: string; desc: string; bg: string; text: string; darkBg: string; darkText: string }> = {
    'N5': {color: 'bg-blue-500', bg: 'bg-blue-50', text: 'text-blue-700', darkBg: 'dark:bg-blue-900/30', darkText: 'dark:text-blue-300', desc: '왕초보 탈출! 기초 단어'},
    'N4': {color: 'bg-green-500', bg: 'bg-green-50', text: 'text-green-700', darkBg: 'dark:bg-green-900/30', darkText: 'dark:text-green-300', desc: '기본적인 회화 단어'},
    'N3': {color: 'bg-yellow-500', bg: 'bg-yellow-50', text: 'text-yellow-700', darkBg: 'dark:bg-yellow-900/30', darkText: 'dark:text-yellow-300', desc: '일상 생활 마스터'},
    'N2': {color: 'bg-orange-500', bg: 'bg-orange-50', text: 'text-orange-700', darkBg: 'dark:bg-orange-900/30', darkText: 'dark:text-orange-300', desc: '비즈니스와 심화 단어'},
    'N1': {color: 'bg-red-500', bg: 'bg-red-50', text: 'text-red-700', darkBg: 'dark:bg-red-900/30', darkText: 'dark:text-red-300', desc: '네이티브 수준의 어휘'},
};

const getApiErrorMessage = (error: unknown, fallbackMessage: string) => {
    if (typeof error === 'object' && error !== null && 'response' in error) {
        const response = (error as { response?: { data?: { message?: string } } }).response;
        return response?.data?.message || fallbackMessage;
    }

    return fallbackMessage;
};

const WordsByLevel = () => {
    // --- 상태 관리 ---
    const [viewMode, setViewMode] = useState<ViewMode>('LEVEL_SELECT');
    const [selectedLevel, setSelectedLevel] = useState<string>('N5');
    const [selectedChapter, setSelectedChapter] = useState<number>(1);
    const {isAuthenticated} = useAuth();
    const [showLoginPopup, setShowLoginPopup] = useState(false);

    // 챕터 수 설정
    const CHAPTER_COUNTS: { [key: string]: number } = {
        'N5': 14, 'N4': 14, 'N3': 43, 'N2': 39, 'N1': 54,
    };
    const currentMaxChapters = CHAPTER_COUNTS[selectedLevel] || 100;

    // 학습 데이터
    const [words, setWords] = useState<Word[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(false);

    // --- 핸들러 ---

    const handleLevelSelect = (level: string) => {
        setSelectedLevel(level);
        setViewMode('CHAPTER_SELECT');
    };

    // 챕터 선택 시 단어 목록 조회 (GET /api/learning/words)
    const handleChapterSelect = async (chapter: number) => {
        if (!isAuthenticated) {
            setShowLoginPopup(true); // 비로그인 시 팝업 노출하고 진행 중단
            return;
        }
        setSelectedChapter(chapter);
        setLoading(true);
        setViewMode('LEARNING');
        setCurrentIndex(0);

        try {
            const levelNum = parseInt(selectedLevel.replace('N', ''));

            // ✨ API 경로 변경: /api/words -> /api/learning/words
            // 이제 백엔드가 토큰을 보고 'isMemorized' 값을 채워서 보내줍니다.
            const response = await api.get('/api/learning/words', {
                params: {
                    level: levelNum,
                    page: chapter
                }
            });
            setWords(response.data);
        } catch (error) {
            console.error("Failed to load words:", error);
            alert("단어를 불러오지 못했습니다. 로그인 상태를 확인해주세요.");
            setViewMode('CHAPTER_SELECT');
        } finally {
            setLoading(false);
        }
    };

    // ✨ 학습 상태 업데이트 (POST /api/learning/progress)
    const handleUpdateProgress = async (wordId: number, isMemorized: boolean): Promise<boolean> => {
        try {
            // 1. 서버에 상태 전송
            await api.post('/api/learning/progress', {
                wordId,
                isMemorized
            });

            // 2. 로컬 상태 즉시 업데이트 (UI 반응성)
            setWords(prevWords =>
                prevWords.map(word =>
                    word.id === wordId ? {...word, isMemorized} : word
                )
            );
            return true;

        } catch (error) {
            console.error("Progress update failed:", error);
            alert(getApiErrorMessage(error, "학습 상태를 저장하지 못했습니다. 로그인 상태를 확인해 주세요."));
            return false;
        }
    };

    const handleNext = () => {
        if (currentIndex < words.length - 1) setCurrentIndex(prev => prev + 1);
    };

    const handlePrev = () => {
        if (currentIndex > 0) setCurrentIndex(prev => prev - 1);
    };

    // --- 렌더링 ---
    const theme = LEVEL_CONFIG[selectedLevel] || LEVEL_CONFIG['N5'];

    if (viewMode === 'LEVEL_SELECT') {
        return (
            <div className="space-y-8 animate-fade-in py-8">
                <div className="text-center space-y-3">
                    <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100 flex items-center justify-center gap-2">
                        <Library className="w-8 h-8 text-indigo-600 dark:text-indigo-400"/>
                        단어장 선택
                    </h2>
                    <p className="text-gray-500 dark:text-gray-400">학습할 JLPT 급수를 선택해주세요.</p>
                </div>
                <div
                    className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5 px-4 max-w-6xl mx-auto">
                    {Object.keys(LEVEL_CONFIG).map((level) => (
                        <button
                            key={level}
                            onClick={() => handleLevelSelect(level)}
                            className="group relative flex flex-col items-center justify-center p-8 h-64 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-3xl shadow-sm hover:shadow-xl hover:-translate-y-2 transition-all duration-300"
                        >
                            <div
                                className={`w-20 h-20 rounded-full flex items-center justify-center text-white text-2xl font-bold mb-6 shadow-lg transform group-hover:scale-110 transition-transform duration-300 ${LEVEL_CONFIG[level].color}`}>
                                {level}
                            </div>
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-2">{level} 단어장</h3>
                            <p className="text-xs text-gray-400 dark:text-gray-500 text-center px-2 font-medium">{LEVEL_CONFIG[level].desc}</p>
                        </button>
                    ))}
                </div>
            </div>
        );
    }

    if (viewMode === 'CHAPTER_SELECT') {
        return (
            <div className="space-y-6 animate-fade-in max-w-5xl mx-auto py-6">
                <div className="flex items-center gap-4 px-4 mb-8">
                    <button onClick={() => setViewMode('LEVEL_SELECT')}
                            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors">
                        <ArrowLeft className="w-6 h-6"/>
                    </button>
                    <div>
                        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                            <span
                                className={`px-3 py-1 rounded-lg text-sm ${theme.bg} ${theme.darkBg} ${theme.text} ${theme.darkText}`}>{selectedLevel}</span>
                            챕터 선택
                        </h2>
                        <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">학습할 구간을 선택하세요 (50단어 씩)</p>
                    </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 px-4">
                    {Array.from({length: currentMaxChapters}, (_, i) => i + 1).map((chapter) => (
                        <button
                            key={chapter}
                            onClick={() => handleChapterSelect(chapter)}
                            className="flex flex-col items-start p-5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:border-indigo-500 dark:hover:border-indigo-400 hover:shadow-md hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-all duration-200 text-left group"
                        >
                            <span
                                className="text-xs font-semibold text-gray-400 dark:text-gray-500 group-hover:text-indigo-500 dark:group-hover:text-indigo-400 mb-1">CHAPTER</span>
                            <span
                                className="text-xl font-bold text-gray-800 dark:text-gray-100 group-hover:text-indigo-700 dark:group-hover:text-indigo-300">{chapter}</span>
                            <div className="w-full h-1 bg-gray-100 dark:bg-gray-700 mt-3 rounded-full overflow-hidden">
                                <div
                                    className="w-0 h-full bg-indigo-500 group-hover:w-full transition-all duration-500"/>
                            </div>
                        </button>
                    ))}
                </div>
                {/* ✨ 로그인 안내 모달 */}
                {showLoginPopup && (
                    <div
                        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center animate-fade-in p-4">
                        <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 max-w-sm w-full text-center shadow-2xl relative">
                            <button
                                onClick={() => setShowLoginPopup(false)}
                                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                            >
                                <X className="w-5 h-5"/>
                            </button>
                            <div
                                className="w-16 h-16 bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400 rounded-full flex items-center justify-center mx-auto mb-4">
                                <AlertCircle className="w-8 h-8"/>
                            </div>
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-2">로그인이 필요합니다</h3>
                            <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
                                단어 학습 기록을 저장하기 위해<br/>먼저 로그인을 진행해 주세요.
                            </p>
                            <button
                                onClick={() => setShowLoginPopup(false)}
                                className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl transition-colors"
                            >
                                확인
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // 3. 학습 모드 (분리된 컴포넌트 사용)
    return (
        <LearningScreen
            level={selectedLevel}
            chapter={selectedChapter}
            words={words}
            currentIndex={currentIndex}
            loading={loading}
            theme={theme}
            onBack={() => setViewMode('CHAPTER_SELECT')}
            onNext={handleNext}
            onPrev={handlePrev}
            onUpdateProgress={handleUpdateProgress} // ✨ 업데이트 함수 전달
        />
    );
};

export default WordsByLevel;
